(() => {
  const root = document.querySelector('[data-orie-quiz]');
  if (!root) return;

  const config = window.OrieQuizConfig || {};
  const prefix = config.storagePrefix || 'orie_quiz_v4';
  const dismissKey = `${prefix}_dismissed_until`;
  const successKey = `${prefix}_subscribed`;

  const dialog = root.querySelector('.orie-quiz__dialog');
  const form = root.querySelector('[data-orie-quiz-form]');
  const steps = [...root.querySelectorAll('[data-orie-step]')];
  const dots = [...root.querySelectorAll('[data-orie-dot]')];
  const backButton = root.querySelector('[data-orie-back]');
  const submitButton = root.querySelector('[data-orie-submit]');
  const successPanel = root.querySelector('[data-orie-success]');
  const closeButtons = root.querySelectorAll('[data-orie-quiz-close]');

  const state = {
    step: 1,
    interests: [],
    support: '',
    opened: false
  };

  const now = () => Date.now();

  const shouldSuppress = () => {
    const dismissedUntil = Number(localStorage.getItem(dismissKey) || 0);
    return localStorage.getItem(successKey) === 'true' || dismissedUntil > now();
  };

  const saveDismissed = () => {
    const days = Number(config.dismissDays || 30);
    localStorage.setItem(dismissKey, String(now() + days * 24 * 60 * 60 * 1000));
  };

  const saveSubscribed = () => {
    localStorage.setItem(successKey, 'true');
    localStorage.removeItem(dismissKey);
  };

  const clearErrors = () => {
    root.querySelectorAll('[data-orie-error]').forEach((error) => {
      error.hidden = true;
    });
  };

  const showError = (name, message) => {
    const error = root.querySelector(`[data-orie-error="${name}"]`);
    if (!error) return;
    if (message) error.textContent = message;
    error.hidden = false;
  };

  const updateHiddenFields = () => {
    const interestField = root.querySelector('[data-orie-field="interest"]');
    const supportField = root.querySelector('[data-orie-field="support"]');

    if (interestField) interestField.value = state.interests.join(', ');
    if (supportField) supportField.value = state.support;
  };

  const showStep = (stepNumber) => {
    state.step = stepNumber;
    clearErrors();

    steps.forEach((step) => {
      step.classList.toggle('is-active', Number(step.dataset.orieStep) === stepNumber);
    });

    dots.forEach((dot) => {
      dot.classList.toggle('is-active', Number(dot.dataset.orieDot) === stepNumber);
    });

    backButton.hidden = stepNumber === 1;

    submitButton.hidden = false;
    submitButton.disabled = false;
    submitButton.classList.remove('is-loading');
    submitButton.textContent = stepNumber === 3 ? 'Join the Orie Community' : 'Next';

    const activeStep = root.querySelector(`[data-orie-step="${stepNumber}"]`);
    const firstInput = activeStep?.querySelector('button, input');

    if (firstInput) {
      setTimeout(() => firstInput.focus({ preventScroll: true }), 150);
    }
  };

  const openQuiz = () => {
    if (state.opened || shouldSuppress()) return;

    state.opened = true;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('orie-quiz-lock');

    setTimeout(() => dialog.focus(), 100);
  };

  const closeQuiz = ({ persist = true } = {}) => {
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('orie-quiz-lock');

    if (persist) saveDismissed();
  };

  const selectInterest = (button) => {
    const value = button.dataset.value;
    const selected = state.interests.includes(value);

    if (selected) {
      state.interests = state.interests.filter((item) => item !== value);
    } else {
      state.interests.push(value);
    }

    button.classList.toggle('is-selected', !selected);
    button.setAttribute('aria-checked', !selected ? 'true' : 'false');

    updateHiddenFields();
    clearErrors();
  };

  const selectSupport = (button) => {
    const value = button.dataset.value;
    state.support = value;

    root.querySelectorAll('[data-orie-option="support"]').forEach((option) => {
      const selected = option === button;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-checked', selected ? 'true' : 'false');
    });

    updateHiddenFields();
    clearErrors();
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateStep = () => {
    clearErrors();

    if (state.step === 1 && state.interests.length === 0) {
      showError('interest', 'Please select at least one interest area.');
      return false;
    }

    if (state.step === 2 && !state.support) {
      showError('support', 'Please select a support type.');
      return false;
    }

    if (state.step === 3) {
      const firstName = form.querySelector('[name="FNAME"]').value.trim();
      const email = form.querySelector('[name="EMAIL"]').value.trim();

      if (!firstName || !isValidEmail(email)) {
        showError('details', 'Please enter your first name and a valid email address.');
        return false;
      }
    }

    return true;
  };

  const submitToWorker = async () => {
    const endpoint = config.workerEndpoint;

    if (!endpoint) {
      throw new Error('Missing Worker endpoint.');
    }

    const payload = {
      email: form.querySelector('[name="EMAIL"]').value.trim(),
      firstName: form.querySelector('[name="FNAME"]').value.trim(),
      interest: state.interests.join(', '),
      support: state.support
    };

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Something went wrong. Please try again.');
    }

    return data;
  };

  const showSuccess = () => {
    saveSubscribed();
    form.hidden = true;
    successPanel.hidden = false;

    setTimeout(() => {
      closeQuiz({ persist: false });
    }, 7000);
  };

  root.querySelectorAll('[data-orie-option]').forEach((button) => {
    button.addEventListener('click', () => {
      if (button.dataset.orieOption === 'interest') {
        selectInterest(button);
      }

      if (button.dataset.orieOption === 'support') {
        selectSupport(button);
      }
    });
  });

  backButton.addEventListener('click', () => {
    showStep(Math.max(state.step - 1, 1));
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    if (!validateStep()) return;

    if (state.step < 3) {
      showStep(state.step + 1);
      return;
    }

    clearErrors();
    submitButton.classList.add('is-loading');
    submitButton.disabled = true;
    form.classList.add('is-submitting');

    try {
      await submitToWorker();
      form.classList.remove('is-submitting');
      showSuccess();
    } catch (error) {
      form.classList.remove('is-submitting');
      submitButton.classList.remove('is-loading');
      submitButton.disabled = false;
      showError('details', error.message || 'Something went wrong. Please try again.');
    }
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', () => closeQuiz({ persist: true }));
  });

  document.addEventListener('keydown', (event) => {
    if (!root.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      closeQuiz({ persist: true });
    }
  });

  setTimeout(openQuiz, 2000);

  const onScroll = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const height = document.documentElement.scrollHeight - window.innerHeight;

    if (height > 0 && scrollTop / height >= 0.35) {
      openQuiz();
      window.removeEventListener('scroll', onScroll);
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });

  showStep(1);
})();